import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { format } from 'date-fns';
import {
  CalendarDays,
  Plus,
  Trash2,
  Edit,
  Calendar as CalendarIcon,
  Check,
  X,
  Clock,
  Loader2,
  Briefcase,
  Heart,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual Leave', icon: Briefcase, color: 'bg-blue-500' },
  { value: 'sick', label: 'Sick Leave', icon: Heart, color: 'bg-red-500' },
  { value: 'excuse', label: 'Excuse Leave', icon: AlertCircle, color: 'bg-yellow-500' },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500'
};

export const LeaveTracking = () => {
  const [searchParams] = useSearchParams();
  const personnelIdParam = searchParams.get('personnel');
  const { api } = useAuth();
  
  const [leaves, setLeaves] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editLeave, setEditLeave] = useState(null);
  const [filterPersonnel, setFilterPersonnel] = useState(personnelIdParam || 'all');
  const [filterType, setFilterType] = useState('all');
  const [leaveSummary, setLeaveSummary] = useState(null);
  
  const [formData, setFormData] = useState({
    personnel_id: personnelIdParam || '',
    leave_type: '',
    start_date: null,
    end_date: null,
    reason: ''
  });

  const fetchData = async () => {
    try {
      const [leavesRes, persRes] = await Promise.all([
        api.get('/leaves', { params: personnelIdParam ? { personnel_id: personnelIdParam } : {} }),
        api.get('/personnel', { params: { is_active: true } })
      ]);
      setLeaves(leavesRes.data);
      setPersonnel(persRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveSummary = async (personnelId) => {
    if (!personnelId || personnelId === 'all') {
      setLeaveSummary(null);
      return;
    }
    try {
      const response = await api.get(`/leaves/summary/${personnelId}`);
      setLeaveSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch leave summary');
    }
  };

  useEffect(() => {
    fetchData();
  }, [api, personnelIdParam]);

  useEffect(() => {
    fetchLeaveSummary(filterPersonnel);
  }, [filterPersonnel]);

  const handleOpenDialog = (leave = null) => {
    if (leave) {
      setEditLeave(leave);
      setFormData({
        personnel_id: leave.personnel_id,
        leave_type: leave.leave_type,
        start_date: new Date(leave.start_date),
        end_date: new Date(leave.end_date),
        reason: leave.reason || ''
      });
    } else {
      setEditLeave(null);
      setFormData({
        personnel_id: filterPersonnel !== 'all' ? filterPersonnel : '',
        leave_type: '',
        start_date: null,
        end_date: null,
        reason: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.personnel_id || !formData.leave_type || !formData.start_date || !formData.end_date) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.end_date < formData.start_date) {
      toast.error('End date must be after start date');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        personnel_id: formData.personnel_id,
        leave_type: formData.leave_type,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(formData.end_date, 'yyyy-MM-dd'),
        reason: formData.reason
      };

      if (editLeave) {
        const response = await api.put(`/leaves/${editLeave.id}`, payload);
        setLeaves(prev => prev.map(l => l.id === editLeave.id ? response.data : l));
        toast.success('Leave updated successfully');
      } else {
        const response = await api.post('/leaves', payload);
        setLeaves(prev => [...prev, response.data]);
        toast.success('Leave request created successfully');
      }
      setDialogOpen(false);
      fetchLeaveSummary(filterPersonnel);
    } catch (error) {
      toast.error('Failed to save leave request');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (leaveId, status) => {
    try {
      const response = await api.put(`/leaves/${leaveId}`, { status });
      setLeaves(prev => prev.map(l => l.id === leaveId ? response.data : l));
      toast.success(`Leave ${status}`);
      fetchLeaveSummary(filterPersonnel);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/leaves/${deleteId}`);
      setLeaves(prev => prev.filter(l => l.id !== deleteId));
      toast.success('Leave deleted successfully');
      fetchLeaveSummary(filterPersonnel);
    } catch (error) {
      toast.error('Failed to delete leave');
    } finally {
      setDeleteId(null);
    }
  };

  const getPersonnelName = (id) => {
    const person = personnel.find(p => p.id === id);
    return person ? `${person.name} ${person.surname}` : 'Unknown';
  };

  const filteredLeaves = leaves.filter(leave => {
    const matchesPersonnel = filterPersonnel === 'all' || leave.personnel_id === filterPersonnel;
    const matchesType = filterType === 'all' || leave.leave_type === filterType;
    return matchesPersonnel && matchesType;
  });

  if (loading) {
    return (
      <div className="page-transition space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leave Tracking</h1>
          <p className="text-muted-foreground">
            Manage annual, sick, and excuse leave requests
          </p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-primary"
          data-testid="add-leave-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Leave Request
        </Button>
      </div>

      {/* Leave Summary (when personnel selected) */}
      {leaveSummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LEAVE_TYPES.map((type) => {
            const summary = leaveSummary[type.value];
            return (
              <Card key={type.value} data-testid={`summary-${type.value}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${type.color}/10`}>
                      <type.icon className={`w-5 h-5 ${type.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{type.label}</p>
                      <p className="text-lg font-bold">
                        {summary.used} / {summary.total} days
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {summary.remaining} remaining
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterPersonnel} onValueChange={setFilterPersonnel}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-personnel-leave">
                <SelectValue placeholder="Filter by personnel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Personnel</SelectItem>
                {personnel.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} {p.surname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-leave-type">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {LEAVE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leave Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Personnel</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeaves.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <CalendarDays className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No leave requests found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLeaves.map((leave) => {
                  const leaveType = LEAVE_TYPES.find(t => t.value === leave.leave_type);
                  return (
                    <TableRow key={leave.id} className="table-row-hover" data-testid={`leave-row-${leave.id}`}>
                      <TableCell className="font-medium">
                        {getPersonnelName(leave.personnel_id)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <div className={`w-2 h-2 rounded-full ${leaveType?.color}`} />
                          {leaveType?.label || leave.leave_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{leave.days} days</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={cn(
                            "gap-1 capitalize",
                            leave.status === 'approved' && "border-green-500 text-green-600",
                            leave.status === 'rejected' && "border-red-500 text-red-600",
                            leave.status === 'pending' && "border-yellow-500 text-yellow-600"
                          )}
                        >
                          <div className={`w-2 h-2 rounded-full ${STATUS_COLORS[leave.status]}`} />
                          {leave.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {leave.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleStatusChange(leave.id, 'approved')}
                                data-testid={`approve-${leave.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleStatusChange(leave.id, 'rejected')}
                                data-testid={`reject-${leave.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(leave)}
                            data-testid={`edit-${leave.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteId(leave.id)}
                            data-testid={`delete-${leave.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editLeave ? 'Edit Leave Request' : 'New Leave Request'}
            </DialogTitle>
            <DialogDescription>
              {editLeave ? 'Update the leave request details' : 'Create a new leave request for personnel'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Personnel *</Label>
              <Select
                value={formData.personnel_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, personnel_id: value }))}
              >
                <SelectTrigger data-testid="leave-personnel-select">
                  <SelectValue placeholder="Select personnel" />
                </SelectTrigger>
                <SelectContent>
                  {personnel.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select
                value={formData.leave_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, leave_type: value }))}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.start_date && "text-muted-foreground"
                      )}
                      data-testid="start-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.start_date ? format(formData.start_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.end_date && "text-muted-foreground"
                      )}
                      data-testid="end-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.end_date ? format(formData.end_date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Optional reason for leave"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-leave-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editLeave ? 'Update' : 'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Leave Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this leave request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-leave"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeaveTracking;
